/**
 * Things a member can put on their own island.
 *
 * The seven that came before these were the world builder's, drawn for curriculum islands: a
 * lighthouse, a campfire, a barrel, a crate, a noticeboard, a boat, a palm. They are all still here
 * and all still placeable — a member arranging their own beach has as much use for a barrel as an
 * admin does. What was missing was everything you would put in a place you actually live in.
 *
 * The rules, all inherited rather than re-decided:
 *
 *   - **16 wide.** The tile is sixteen and so is the sprite; `lift` raises anything that should read
 *     as taller than the ground it stands on, which is the entire difference between a lamp and a
 *     lamp-post.
 *   - **Nothing is solid.** Not one of these blocks a step. That is what makes it structurally
 *     impossible for an arrangement of furniture to wall somebody in — no reachability check has to
 *     think about decoration, only about painted terrain. Want a wall? Paint a fence.
 *   - **Colours come from the shared keys** in `sprites.ts`, so a palette change moves all of them
 *     at once and none of them can drift away from the world they stand in.
 *
 * `node scripts/sheet.mjs` draws them all for review. A test proves a sprite is sixteen wide; it
 * cannot prove it looks like a hammock.
 */

import type { PixelRows } from './sprites';

/* ------------------------------------------------------------------ sitting and working */

/** A chair, three-quarter on, because a chair drawn flat-on reads as a ladder. */
export const CHAIR: PixelRows = [
  '................',
  '................',
  '................',
  '.....kkkkkk.....',
  '.....koooook....',
  '.....kooooOk....',
  '.....kooooOk....',
  '.....kooooOk....',
  '...kkkooooOkk...',
  '...koooooooOk...',
  '...kOOOOOOOOk...',
  '...kkkkkkkkkk...',
  '....k.O..O.k....',
  '....k.O..O.k....',
  '....kkO..Okk....',
  '................',
];

export const TABLE: PixelRows = [
  '................',
  '................',
  '................',
  '................',
  '..kkkkkkkkkkkk..',
  '..koooooooooOk..',
  '..kOOOOOOOOOOk..',
  '..kkkkkkkkkkkk..',
  '....kOk..kOk....',
  '....kOk..kOk....',
  '....kOk..kOk....',
  '....kOk..kOk....',
  '...kkOkkkkOkk...',
  '...kOOkkkkOOk...',
  '...kkkkkkkkkk...',
  '................',
];

/** The desk with a screen on it. The one object on this island that is about the work. */
export const DESK: PixelRows = [
  '................',
  '.....kkkkkk.....',
  '....kaaaaaak....',
  '....kaddddak....',
  '....kadddda k...',
  '....kaaaaaak....',
  '.....kkkkkk.....',
  '......kOOk......',
  '..kkkkkkkkkkkk..',
  '..koooooooooOk..',
  '..kOOOOOOOOOOk..',
  '..kkkkkkkkkkkk..',
  '...kOk....kOk...',
  '...kOk....kOk...',
  '...kkk....kkk...',
  '................',
];

export const BOOKSHELF: PixelRows = [
  '................',
  '..kkkkkkkkkkkk..',
  '..koaokzkeaokk..',
  '..koaokzkeaokk..',
  '..kOOOOOOOOOOk..',
  '..kzokakoykokk..',
  '..kzokakoykokk..',
  '..kOOOOOOOOOOk..',
  '..keokzkaokokk..',
  '..keokzkaokokk..',
  '..kOOOOOOOOOOk..',
  '..kokykzkeaokk..',
  '..kokykzkeaokk..',
  '..kOOOOOOOOOOk..',
  '..kkkkkkkkkkkk..',
  '................',
];

/* ------------------------------------------------------------------ light and warmth */

/** A standing lamp. Lifted, so the light sits above head height rather than on the sand. */
export const LAMP: PixelRows = [
  '.....kkkkkk.....',
  '....kyyyyyyk....',
  '....kywwwwyk....',
  '....kywwwwyk....',
  '....kyyyyyyk....',
  '.....kkkkkk.....',
  '......kOOk......',
  '......kOOk......',
  '......kOOk......',
  '......kOOk......',
  '......kOOk......',
  '......kOOk......',
  '.....kkOOkk.....',
  '....kOOOOOOk....',
  '....kkkkkkkk....',
  '................',
];

/** A hanging lantern on a hook. Small, warm, and the cheapest thing to put by a door. */
export const LANTERN: PixelRows = [
  '................',
  '................',
  '......kkkk......',
  '.....kOOOOk.....',
  '.....kkkkkk.....',
  '.....kyyyyk.....',
  '....kywwwwyk....',
  '....kywwwwyk....',
  '....kywwwwyk....',
  '.....kyyyyk.....',
  '.....kkkkkk.....',
  '......kOOk......',
  '......kkkk......',
  '................',
  '................',
  '................',
];

/* ------------------------------------------------------------------ outside */

export const BENCH: PixelRows = [
  '................',
  '................',
  '................',
  '..kkkkkkkkkkkk..',
  '..koooooooooOk..',
  '..kOOOOOOOOOOk..',
  '..kkkkkkkkkkkk..',
  '..koooooooooOk..',
  '..kOOOOOOOOOOk..',
  '..kkkkkkkkkkkk..',
  '...kOk....kOk...',
  '...kOk....kOk...',
  '...kOk....kOk...',
  '...kkk....kkk...',
  '................',
  '................',
];

/**
 * A beach umbrella — and it is cyan and white rather than red and white for one reason: drawn in the
 * obvious colours at sixteen pixels it was a toadstool on a stick, and there is a real mushroom two
 * squares along in the same palette. The canopy is also wider than it is tall, which is the other
 * half of what tells the two apart at a glance.
 */
export const UMBRELLA: PixelRows = [
  '................',
  '.......kk.......',
  '....kkkaakkk....',
  '..kkaaawwaaakk..',
  '.kaaawwAAwwaaak.',
  '.kAAwwAAAAwwAAk.',
  '.kkkkkkkkkkkkkk.',
  '.......kk.......',
  '.......kOk......',
  '.......kOk......',
  '.......kOk......',
  '.......kOk......',
  '......kkOkk.....',
  '......kOOOk.....',
  '......kkkkk.....',
  '................',
];

export const HAMMOCK: PixelRows = [
  '................',
  '................',
  '..k..........k..',
  '..k..........k..',
  '..k.kkkkkkkk.k..',
  '..kkdDddDddDkk..',
  '..k.dDddDddD.k..',
  '..k..kddDdk..k..',
  '..k...kkkk...k..',
  '..k..........k..',
  '..k..........k..',
  '..k..........k..',
  '..k..........k..',
  '.kNk........kNk.',
  '.kkk........kkk.',
  '................',
];

export const TENT: PixelRows = [
  '................',
  '................',
  '.......kk.......',
  '......keek......',
  '.....keeeek.....',
  '....keeEeeek....',
  '...keeEkkEeek...',
  '..keeEkooked.k..',
  '..keEkoooookdk..',
  '.keEkooooooOkdk.',
  '.kEkooooooooOkk.',
  '.kkOOOOOOOOOOkk.',
  '.kkkkkkkkkkkkkk.',
  '................',
  '................',
  '................',
];

/** A well. The only thing here with a hole in it, which is why it is worth the pixels. */
export const WELL: PixelRows = [
  '.....kkkkkk.....',
  '....kOOOOOOk....',
  '....kkkkkkkk....',
  '.....kOk.kOk....',
  '.....kOk.kOk....',
  '.....kOk.kOk....',
  '..kkkkkkkkkkkk..',
  '..krrrrrrrrrrk..',
  '..krkkkkkkkkrk..',
  '..krkmmmmmmkrk..',
  '..kRkmmmmmmkRk..',
  '..kRkkkkkkkkRk..',
  '..kRRRRRRRRRRk..',
  '..kkkkkkkkkkkk..',
  '................',
  '................',
];

/* ------------------------------------------------------------------ growing things */

export const PLANTER: PixelRows = [
  '................',
  '................',
  '......kk.k......',
  '.....kzzkzk.....',
  '....kgkzzkgk....',
  '....kgkkkkgk....',
  '.....kgggk......',
  '......kgk.......',
  '..kkkkkkkkkkk...',
  '..koooooooook...',
  '..kOOOOOOOOOk...',
  '...kOOOOOOOk....',
  '...kkkkkkkkk....',
  '................',
  '................',
  '................',
];

export const STUMP: PixelRows = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '...kkkkkkkkkk...',
  '..knnnnnnnnnnk..',
  '..knNNNNNNNNnk..',
  '..knNnnnnnnNnk..',
  '..knNnNNNNnNnk..',
  '..knNnnnnnnNnk..',
  '..knNNNNNNNNnk..',
  '..kNNNNNNNNNNk..',
  '..kkkkkkkkkkkk..',
  '................',
];

export const MUSHROOM: PixelRows = [
  '................',
  '................',
  '................',
  '................',
  '......kkkk......',
  '....kkzzzzkk....',
  '...kzzwzzwzzk...',
  '..kzzzzwzzzzzk..',
  '..kzwzzzzzwzzk..',
  '..kkkkkkkkkkkk..',
  '....kddddddk....',
  '....kdDDDDdk....',
  '....kdDDDDdk....',
  '....kkkkkkkk....',
  '................',
  '................',
];

/* ------------------------------------------------------------------ the shore */

export const SHELL: PixelRows = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '......kkkk......',
  '....kkdwwdkk....',
  '...kdwdwwdwdk...',
  '..kdwdwdwdwdwk..',
  '..kdwdwdwdwdwk..',
  '..kkdkdkdkdkkk..',
  '...kkkkkkkkkk...',
  '................',
  '................',
];

export const BUOY: PixelRows = [
  '................',
  '.......k........',
  '.......ky.......',
  '.......kyk......',
  '......kkykk.....',
  '.....kzzzzzk....',
  '....kzddddzzk...',
  '....kzddddzzk...',
  '....kzzzzzzzk...',
  '....kddddddzk...',
  '....kddddddzk...',
  '.....kzzzzzk....',
  '......kkkkk.....',
  '................',
  '................',
  '................',
];

/**
 * A pile of nets over a float, in rope colours rather than green: drawn in green it was a clump of
 * seaweed, which the shoreline already has plenty of. The two red floats are what make it read as
 * fishing gear rather than as anything that grew there.
 */
export const NETS: PixelRows = [
  '................',
  '................',
  '................',
  '................',
  '.......kk.......',
  '......kzzk......',
  '....kkzddzkk....',
  '...kOkzzzzkOk...',
  '..kOoOkkkkOoOk..',
  '..koOoOoOoOoOk..',
  '.kOoOoOoOoOoOok.',
  '.koOoOoOoOoOoOk.',
  '.kOoOoOoOoOoOok.',
  '.kkkkkkkkkkkkkk.',
  '................',
  '................',
];

/* ------------------------------------------------------------------ standing things */

/** A stone marker. The one thing here that reads as deliberate rather than lived-in. */
export const STATUE: PixelRows = [
  '.....kkkkkk.....',
  '....krrrrrrk....',
  '....krrkkrrk....',
  '....krrrrrrk....',
  '....kRrrrrRk....',
  '.....krrrrk.....',
  '.....krrrrk.....',
  '....krrrrrrk....',
  '....kRRRRRRk....',
  '...kkkkkkkkkk...',
  '..krrrrrrrrrrk..',
  '..kRRRRRRRRRRk..',
  '..kRRRRRRRRRRk..',
  '..kkkkkkkkkkkk..',
  '................',
  '................',
];

export const LADDER: PixelRows = [
  '................',
  '...kOk....kOk...',
  '...kOk....kOk...',
  '...kOkkkkkkOk...',
  '...kOooooooOk...',
  '...kOkkkkkkOk...',
  '...kOk....kOk...',
  '...kOkkkkkkOk...',
  '...kOooooooOk...',
  '...kOkkkkkkOk...',
  '...kOk....kOk...',
  '...kOkkkkkkOk...',
  '...kOooooooOk...',
  '...kOkkkkkkOk...',
  '...kOk....kOk...',
  '...kkk....kkk...',
];

export const DRUM: PixelRows = [
  '................',
  '................',
  '................',
  '....kkkkkkkk....',
  '...kdddddddddk..',
  '...kdDDDDDDDdk..',
  '...kkkkkkkkkkk..',
  '...kzoooooozk...',
  '...kozzzzzzok...',
  '...kzoooooozk...',
  '...kozzzzzzok...',
  '...kzoooooozk...',
  '...kkkkkkkkkk...',
  '....kOOOOOOk....',
  '....kkkkkkkk....',
  '................',
];

/** A stack of two crates. Cheap depth, and it makes a corner look used. */
export const CRATES: PixelRows = [
  '................',
  '..kkkkkkkk......',
  '..koOooooOk.....',
  '..koOooooOk.....',
  '..kooOoOook.....',
  '..koOooooOk.....',
  '..kkkkkkkk......',
  '....kkkkkkkkkk..',
  '....kOooooooOk..',
  '....koOoooooOk..',
  '....kooOoooOok..',
  '....koOoooooOk..',
  '....kOooooooOk..',
  '....kkkkkkkkkk..',
  '................',
  '................',
];

/** A short signpost with nothing written on it. The words live on Skool. */
export const POST: PixelRows = [
  '................',
  '................',
  '...kkkkkkkkkk...',
  '...koooooooOk...',
  '...kOOOOOOOOk...',
  '...koooooooOk...',
  '...kOOOOOOOOk...',
  '...kkkkkkkkkk...',
  '......kOOk......',
  '......kOOk......',
  '......kOOk......',
  '......kOOk......',
  '.....kkOOkk.....',
  '.....kOOOOk.....',
  '.....kkkkkk.....',
  '................',
];

export const FLAG: PixelRows = [
  '.....kkkkkkk....',
  '.....kaaaaak....',
  '.....kawwwak....',
  '.....kaaaaak....',
  '.....kkkkkkk....',
  '.....kOk........',
  '.....kOk........',
  '.....kOk........',
  '.....kOk........',
  '.....kOk........',
  '.....kOk........',
  '.....kOk........',
  '....kkOkk.......',
  '....kOOOk.......',
  '....kkkkk.......',
  '................',
];
